import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, Button, Form, FormGroup, Label, Input } from 'reactstrap';
import * as PapeletaService from '../../axios_services/papeletasalida.service';

// ── Helpers de tiempo ────────────────────────────────────────────────────────
const HOURS = Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, '0'));
const MINUTES = Array.from({ length: 60 }, (_, i) => String(i).padStart(2, '0'));
const PERIODS = ['AM', 'PM'];

const to24h = (h, m, p) => {
    let hr = parseInt(h, 10);
    if (p === 'PM' && hr < 12) hr += 12;
    if (p === 'AM' && hr === 12) hr = 0;
    return `${String(hr).padStart(2, '0')}:${m}`;
};

const from24h = (time24) => {
    if (!time24) return { h: '08', m: '00', p: 'AM' };
    const [hrs, mins] = time24.split(':');
    let h = parseInt(hrs, 10);
    const p = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return { h: String(h).padStart(2, '0'), m: mins.substring(0, 2), p };
};

const formatTime12h = (time24) => {
    if (!time24) return '—';
    const [hrs, mins] = time24.split(':');
    let h = parseInt(hrs, 10);
    const m = mins.substring(0, 2);
    const ampm = h >= 12 ? 'PM' : 'AM';
    h = h % 12 || 12;
    return `${String(h).padStart(2, '0')}:${m} ${ampm}`;
};

const TimeSelector = ({ value, onChange }) => {
    const { h, m, p } = from24h(value);
    const handlePart = (part, val) => {
        let nh = h, nm = m, np = p;
        if (part === 'h') nh = val;
        if (part === 'm') nm = val;
        if (part === 'p') np = val;
        onChange(to24h(nh, nm, np));
    };
    return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
            <select className="form-control" style={{ width: '60px', padding: '4px' }}
                value={h} onChange={e => handlePart('h', e.target.value)}>
                {HOURS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <span style={{ fontWeight: 'bold', fontSize: '16px' }}>:</span>
            <select className="form-control" style={{ width: '60px', padding: '4px' }}
                value={m} onChange={e => handlePart('m', e.target.value)}>
                {MINUTES.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
            <select className="form-control" style={{ width: '68px', padding: '4px' }}
                value={p} onChange={e => handlePart('p', e.target.value)}>
                {PERIODS.map(v => <option key={v} value={v}>{v}</option>)}
            </select>
        </div>
    );
};

// ── Componente principal ──────────────────────────────────────────────────────
const ModalAprobarTH = ({ isOpen, onClose, papeleta, onRefresh }) => {
    const [esRemunerado, setEsRemunerado] = useState(true);
    const [fechaRegreso, setFechaRegreso] = useState('');
    const [tieneRegreso, setTieneRegreso] = useState(false);
    const [horaRegreso, setHoraRegreso] = useState('13:00');
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (isOpen && papeleta) {
            setEsRemunerado(!!Number(papeleta.es_remunerado));
            const fr = papeleta.fecha_regreso;
            setFechaRegreso(fr || papeleta.fecha_salida || '');
            const hr = papeleta.hora_regreso ? papeleta.hora_regreso.substring(0, 5) : '';
            setTieneRegreso(!!hr);
            setHoraRegreso(hr || '13:00');
        }
    }, [isOpen, papeleta?.papeleta_id]);

    const handleAprobar = async () => {
        if (!window.confirm('¿Confirma la aprobación final de esta papeleta?')) return;
        setSaving(true);
        try {
            await PapeletaService.aprobarTH({
                papeleta_id: papeleta.papeleta_id,
                observaciones: '',
                es_remunerado: esRemunerado,
                fecha_regreso: papeleta.motivo === 'PERMISO' ? (fechaRegreso || null) : null,
                hora_regreso: tieneRegreso ? (horaRegreso || null) : null,
            });
            onRefresh();
            onClose();
        } catch (err) {
            alert(err.response?.data?.error || 'Error al aprobar.');
        } finally {
            setSaving(false);
        }
    };

    if (!papeleta) return null;

    return (
        <Modal isOpen={isOpen} className="custom-modal" backdrop="static">
            <ModalHeader className="modal-header">
                <div className="titulo">
                    <h1>APROBAR PAPELETA (ADMINISTRACIÓN)</h1>
                    <Button className="close-btn" onClick={onClose}>X</Button>
                </div>
            </ModalHeader>

            <div className="modal-body-footer">
                <ModalBody className="modal-body">
                    <Form className="fila" onSubmit={e => e.preventDefault()}>

                        {/* Info de referencia */}
                        <FormGroup>
                            <Label>Colaborador:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                value={papeleta.nombre_empleado || ''}
                                readOnly
                                style={{ background: '#f3f4f6', cursor: 'default' }}
                            />
                        </FormGroup>

                        <FormGroup>
                            <Label>Fecha de Salida:</Label>
                            <Input
                                className="form-control"
                                type="text"
                                value={papeleta.fecha_salida || ''}
                                readOnly
                                style={{ background: '#f3f4f6', cursor: 'default' }}
                            />
                        </FormGroup>

                        {/* Fecha Regreso */}
                        {papeleta.motivo === 'PERMISO' && (
                            <FormGroup style={{ minHeight: 'auto' }}>
                                <Label>Fecha de regreso:</Label>
                                <Input
                                    type="date"
                                    value={fechaRegreso}
                                    onChange={e => setFechaRegreso(e.target.value)}
                                    style={{ width: '100%' }}
                                />
                            </FormGroup>
                        )}

                        {papeleta.motivo !== 'PERMISO' && (
                            <>
                                <FormGroup>
                                    <Label>Hora de Salida:</Label>
                                    <Input
                                        className="form-control"
                                        type="text"
                                        value={formatTime12h((papeleta.hora_salida || '').substring(0, 5))}
                                        readOnly
                                        style={{ background: '#f3f4f6', cursor: 'default' }}
                                    />
                                </FormGroup>

                                {/* Hora Regreso */}
                                <FormGroup style={{ minHeight: 'auto' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                                        <Input
                                            type="checkbox"
                                            id="th_tiene_regreso"
                                            checked={tieneRegreso}
                                            onChange={e => setTieneRegreso(e.target.checked)}
                                            style={{ margin: 0, marginTop: '-2px', accentColor: '#0549a7' }}
                                        />
                                        <Label check htmlFor="th_tiene_regreso" style={{ margin: 0 }}>
                                            Indicar hora de regreso
                                        </Label>
                                    </div>
                                    {tieneRegreso && (
                                        <TimeSelector value={horaRegreso} onChange={setHoraRegreso} />
                                    )}
                                </FormGroup>
                            </>
                        )}

                        {/* Tipo */}
                        <FormGroup>
                            <Label>Tipo de Salida:</Label>
                            <Input
                                className="form-control"
                                type="select"
                                value={esRemunerado ? '1' : '0'}
                                onChange={e => setEsRemunerado(e.target.value === '1')}
                            >
                                <option value="1">REMUNERADO</option>
                                <option value="0">NO REMUNERADO</option>
                            </Input>
                        </FormGroup>



                        {/* Botón */}
                        <FormGroup style={{ marginTop: '18px' }}>
                            <div className="button-container">
                                <Button
                                    className="button-custom"
                                    onClick={handleAprobar}
                                    disabled={saving}
                                >
                                    {saving ? 'REGISTRANDO...' : 'REGISTRAR'}
                                </Button>
                            </div>
                        </FormGroup>

                    </Form>
                </ModalBody>
            </div>
        </Modal>
    );
};

export default ModalAprobarTH;
